// Draft persistence in IndexedDB (no dependencies), so a refresh, a dropped call, or a dead
// battery never loses an answer. What is stored, on this device only:
//   draft  { id: 'current', submissionId, adultConfirmed, role, topicId, promptId, step,
//            burnedNos[], photoUpload, uid, createdAt, updatedAt }
//   clips  { id, promptId, data: ArrayBuffer, blobType, durationSec, mimeType, source,
//            createdAt, kept, clipNo, uploaded, storagePath, sizeBytes, uploadedUid }
//          kept:false is a finished take the person has not kept (yet).
//   chunks { takeId, seq, data, blobType, promptId, mimeType, elapsedSec, at }
//          written every second WHILE recording; rebuilt into a take after a crash.
// Audio is stored as ArrayBuffer, not Blob: some Safari versions lose Blobs kept in IndexedDB.
// When IndexedDB is missing or fails (private browsing, storage full), the store reports
// persistent:false and the app keeps everything in memory and tells the person so.
import { logger } from '../lib/log.js';

const log = logger('record');
const DB_NAME = 'oh-record';
const DB_VERSION = 1;

function reqP(r) { return new Promise((resolve, reject) => { r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error); }); }
function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
  });
}

export async function blobToBuffer(blob) {
  if (blob && typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error);
    fr.readAsArrayBuffer(blob);
  });
}

const CLIP_FIELDS = ['id', 'promptId', 'durationSec', 'mimeType', 'source', 'createdAt', 'kept', 'clipNo', 'uploaded', 'storagePath', 'sizeBytes', 'uploadedUid', 'recovered', 'autoStopped'];
function clipMeta(clip) {
  const o = {};
  for (const k of CLIP_FIELDS) if (clip[k] !== undefined) o[k] = clip[k];
  return o;
}

function memoryStore(reason) {
  const noop = async () => null;
  return {
    persistent: false,
    reason,
    getDraft: noop, putDraft: noop,
    getClips: async () => [], putClip: noop, updateClip: noop, deleteClip: noop,
    putChunk: noop, getChunkTakes: async () => [], deleteChunks: noop,
    clearAll: noop,
  };
}

/**
 * Open the store. `onFailure(err)` is called once if a write fails later (quota, private mode),
 * so the page can say recordings now live only in this tab.
 */
export async function openStore({ onFailure } = {}) {
  let idb;
  try { idb = window.indexedDB; } catch { idb = null; }
  if (!idb) return memoryStore('no-indexeddb');

  let db;
  try {
    db = await new Promise((resolve, reject) => {
      let r;
      try { r = idb.open(DB_NAME, DB_VERSION); } catch (e) { reject(e); return; }
      const timer = setTimeout(() => reject(new Error('IndexedDB open timed out')), 5000);
      r.onupgradeneeded = () => {
        const d = r.result;
        if (!d.objectStoreNames.contains('draft')) d.createObjectStore('draft', { keyPath: 'id' });
        if (!d.objectStoreNames.contains('clips')) d.createObjectStore('clips', { keyPath: 'id' });
        if (!d.objectStoreNames.contains('chunks')) d.createObjectStore('chunks', { keyPath: ['takeId', 'seq'] });
      };
      r.onsuccess = () => { clearTimeout(timer); resolve(r.result); };
      r.onerror = () => { clearTimeout(timer); reject(r.error); };
      r.onblocked = () => { clearTimeout(timer); reject(new Error('IndexedDB blocked')); };
    });
  } catch (e) {
    log.warn('storage_unavailable', { reason: 'open_failed', errName: e && e.name, message: e && e.message });
    return memoryStore('open-failed');
  }
  db.onversionchange = () => { try { db.close(); } catch { /* ignore */ } };

  let failed = false;
  const store = { persistent: true, reason: null };
  function fail(op, e) {
    log.warn('store_write_failed', { op, errName: e && e.name, message: e && e.message });
    if (!failed) {
      failed = true;
      store.persistent = false;
      store.reason = 'write-failed';
      if (onFailure) { try { onFailure(e); } catch { /* ignore */ } }
    }
    return null;
  }

  async function run(stores, mode, fn) {
    const tx = db.transaction(stores, mode);
    const out = fn(tx);
    // A failed request also fails the transaction; keep its own rejection from going unhandled.
    if (out && typeof out.catch === 'function') out.catch(() => {});
    await txDone(tx);
    return out;
  }

  store.getDraft = async () => {
    try { return await run(['draft'], 'readonly', (tx) => reqP(tx.objectStore('draft').get('current'))).then((p) => p); }
    catch (e) { log.warn('store_read_failed', { op: 'getDraft', errName: e && e.name }); return null; }
  };
  store.putDraft = async (draft) => {
    try { await run(['draft'], 'readwrite', (tx) => { tx.objectStore('draft').put({ ...draft, id: 'current', updatedAt: new Date().toISOString() }); }); return true; }
    catch (e) { return fail('putDraft', e); }
  };

  store.getClips = async () => {
    try {
      const recs = await run(['clips'], 'readonly', (tx) => reqP(tx.objectStore('clips').getAll())).then((p) => p);
      return (recs || []).map((r) => {
        const { data, blobType, ...meta } = r;
        return { ...meta, blob: new Blob([data], { type: blobType || meta.mimeType || '' }) };
      });
    } catch (e) { log.warn('store_read_failed', { op: 'getClips', errName: e && e.name }); return []; }
  };
  store.putClip = async (clip) => {
    try {
      const data = await blobToBuffer(clip.blob);
      await run(['clips'], 'readwrite', (tx) => { tx.objectStore('clips').put({ ...clipMeta(clip), data, blobType: clip.blob.type || clip.mimeType || '' }); });
      return true;
    } catch (e) { return fail('putClip', e); }
  };
  store.updateClip = async (id, patch) => {
    try {
      await run(['clips'], 'readwrite', (tx) => {
        const os = tx.objectStore('clips');
        const g = os.get(id);
        g.onsuccess = () => { if (g.result) os.put({ ...g.result, ...clipMeta(patch), id }); };
      });
      return true;
    } catch (e) { return fail('updateClip', e); }
  };
  store.deleteClip = async (id) => {
    try { await run(['clips'], 'readwrite', (tx) => { tx.objectStore('clips').delete(id); }); return true; }
    catch (e) { return fail('deleteClip', e); }
  };

  store.putChunk = async (takeId, seq, blob, meta) => {
    try {
      const data = await blobToBuffer(blob);
      await run(['chunks'], 'readwrite', (tx) => {
        tx.objectStore('chunks').put({ takeId, seq, data, blobType: blob.type || '', at: new Date().toISOString(), ...meta });
      });
      return true;
    } catch (e) { return fail('putChunk', e); }
  };
  /** Takes that were being recorded when the page died: [{ takeId, promptId, mimeType, elapsedSec, blob }]. */
  store.getChunkTakes = async () => {
    try {
      const recs = await run(['chunks'], 'readonly', (tx) => reqP(tx.objectStore('chunks').getAll())).then((p) => p);
      const byTake = new Map();
      for (const r of recs || []) {
        if (!byTake.has(r.takeId)) byTake.set(r.takeId, []);
        byTake.get(r.takeId).push(r);
      }
      return [...byTake.entries()].map(([takeId, list]) => {
        list.sort((a, b) => a.seq - b.seq);
        const first = list[0];
        const type = first.blobType || first.mimeType || '';
        return {
          takeId,
          complete: first.seq === 0 && list.every((x, i) => x.seq === i), // the header chunk and no gaps
          promptId: first.promptId,
          mimeType: first.mimeType || type,
          elapsedSec: Math.max(...list.map((x) => Number(x.elapsedSec) || 0)),
          createdAt: first.at,
          blob: new Blob(list.map((x) => x.data), { type }),
        };
      });
    } catch (e) { log.warn('store_read_failed', { op: 'getChunkTakes', errName: e && e.name }); return []; }
  };
  store.deleteChunks = async (takeId) => {
    try {
      await run(['chunks'], 'readwrite', (tx) => { tx.objectStore('chunks').delete(IDBKeyRange.bound([takeId, 0], [takeId, Infinity])); });
      return true;
    } catch (e) { return fail('deleteChunks', e); }
  };

  store.clearAll = async () => {
    try {
      await run(['draft', 'clips', 'chunks'], 'readwrite', (tx) => {
        tx.objectStore('draft').clear(); tx.objectStore('clips').clear(); tx.objectStore('chunks').clear();
      });
      return true;
    } catch (e) { return fail('clearAll', e); }
  };

  return store;
}
